import {useMutation, useQuery, useQueryClient} from '@tanstack/react-query';

import {
  RQ_BILL_DASHBOARD_API,
  RQ_BILL_LOOKUP_BYFILTER_API,
  RQ_CREATE_BILL_API,
  RQ_DELETE_BILL_API,
  RQ_GET_ALL_BILLS_API,
  RQ_GET_BILL_BY_ID_API,
  RQ_GET_DELETED_BILLS_API,
  RQ_PERMANENT_DELETE_BILL_API,
  RQ_PRODUCTS_BY_FILTER_API,
  RQ_RESTORE_BILL_API,
  RQ_UPDATE_BILL_API,
  RQ_UPDATE_PAYMENT,
} from './constants';
import {rqCreateRequest} from './rqHttpUtils';

export const RQGetAllBillsAPI = (token, options = {}) => {
  return useQuery({
    queryKey: ['rq_get_all_bills'],
    queryFn: async () => {
      return await rqCreateRequest(
        RQ_GET_ALL_BILLS_API,
        null,   // body
        token,
        null,   // params
      ) || [];
    },
    enabled: !!token,
    ...options,
  });
};

export const RqGetDeletedBills = (token, options = {}) => {
  return useQuery({
    queryKey: ['rq_get_deleted_bills'],
    queryFn: async () => {
      return await rqCreateRequest(
        RQ_GET_DELETED_BILLS_API,
        null,
        token,
        null,
      ) || [];
    },
    enabled: !!token,
    ...options,
  });
};

export const RqGetBillById = id => {
  return useQuery({
    queryKey: ['rq_get_bill', id],
    queryFn: async () => {
      return rqCreateRequest(
        RQ_GET_BILL_BY_ID_API,
        null,
        null,
        {id},
      );
    },
    enabled: !!id,
  });
};

export const RqCreateBill = (options = {}) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: ['rq_create_bill'],
    mutationFn: async ({token, billData}) => {
      return rqCreateRequest(
        RQ_CREATE_BILL_API,
        billData,
        token,
      );
    },
    onSuccess: (data, variables, context) => {
      queryClient.invalidateQueries({queryKey: ['rq_get_all_bills']});
      options?.onSuccess?.(data, variables, context);
    },
    ...options,
  });
};

export const RqSearchProductByFilter = () => {
  return useMutation({
    mutationKey: ['rq_search_product_filter'],
    mutationFn: async filters => {
      return rqCreateRequest(
        RQ_PRODUCTS_BY_FILTER_API,
        filters,
        null,
        null,
      );
    },
  });
};

export const RqUpdatePayment = (options = {}) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: ['rq_update_payment'],
    mutationFn: async ({billId, paymentData, token}) => {
      return rqCreateRequest(
        RQ_UPDATE_PAYMENT,
        paymentData,
        token,
        {billId}, // replaces <%= billId %>
      );
    },
    onSuccess: (data, variables, context) => {
      queryClient.invalidateQueries({
        queryKey: ['rq_get_all_bills'],
      });
      options?.onSuccess?.(data, variables, context);
    },
    ...options,
  });
};

export const RqDeleteBill = (options = {}) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({id, token}) => {
      return rqCreateRequest(
        RQ_DELETE_BILL_API,
        null,
        token,
        {id},
      );
    },
    onSuccess: (data, variables, context) => {
      queryClient.invalidateQueries({queryKey: ['rq_get_all_bills']});
      queryClient.invalidateQueries({queryKey: ['rq_get_deleted_bills']});
      options?.onSuccess?.(data, variables, context);
    },
    ...options,
  });
};

export const RqRestoreBill = (options = {}) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({id, token}) => {
      return rqCreateRequest(
        RQ_RESTORE_BILL_API,
        null,
        token,
        {id},
      );
    },
    onSuccess: (data, variables, context) => {
      queryClient.invalidateQueries({queryKey: ['rq_get_all_bills']});
      queryClient.invalidateQueries({queryKey: ['rq_get_deleted_bills']});
      options?.onSuccess?.(data, variables, context);
    },
    ...options,
  });
};

export const RqUpdateBill = (options = {}) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({id, billData, token}) => {
      return rqCreateRequest(RQ_UPDATE_BILL_API, billData, token, {id});
    },
    onSuccess: (data, variables, context) => {
      queryClient.invalidateQueries({queryKey: ['rq_get_all_bills']});
      options?.onSuccess?.(data, variables, context);
    },
    ...options,
  });
};

export const RqBillLookupByFilter = (options = {}) => {
  return useMutation({
    mutationKey: ['rq_bill_lookup_by_filter'],
    mutationFn: async ({filters, token}) => {  // ← destructure token
      return rqCreateRequest(
        RQ_BILL_LOOKUP_BYFILTER_API,
        filters,
        token,  
        null,
      );
    },
    ...options,
  });
};

export const RqBillDashboard = token => {
  return useMutation({
    mutationKey: ['rq_bill_dashboard'],
    
    mutationFn: async filters => {
      return rqCreateRequest(
        RQ_BILL_DASHBOARD_API,
        filters,
        token,
        null,
      );
    },
  });
};

export const RqPermanentDeleteBill = (options = {}) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: ['rq_permanent_delete_bill'],
    mutationFn: async ({id, token}) => {
      if (!token) {
        throw new Error('Auth token missing');
      }

      return rqCreateRequest(
        RQ_PERMANENT_DELETE_BILL_API,
        null,
        token,
        {id},
      );
    },
    onSuccess: (data, variables, context) => {
      queryClient.invalidateQueries({queryKey: ['rq_get_all_bills']});
      queryClient.invalidateQueries({queryKey: ['rq_get_deleted_bills']});
      options?.onSuccess?.(data, variables, context);
    },
    ...options,
  });
};
